import { useContext, useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { stringify } from "svgson";
import AppContext from "../contexts/AppContext";
import {
  auth,
  updateSVGInStorage,
  uploadSVGToStorage,
  writeSVGToDatabase,
} from "../lib/firebase/firebase";
import { SVG_FOLDER } from "../config/firebaseVariant";
import { updateUploadedFileInRedux, addNewlyUploadedFile } from "../redux/slices/UploadedFiles";
import { setShowEditor } from "../redux/slices/MyFiles";

// Module-level guard so only one hook instance triggers the auto-upload,
// even though the hook is mounted in multiple components simultaneously.
let autoUploadFileId = null;

const useSaveSVG = (svgJoined, currentFile) => {
  const { svgStore, setSvgStore } = useContext(AppContext);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (
      !svgJoined ||
      !currentFile?.isLocalOnly ||
      svgStore?.firebase
    ) {
      return;
    }

    const fileKey = currentFile.name || currentFile.id || "local-svg";
    if (autoUploadFileId === fileKey) return;
    autoUploadFileId = fileKey;

    (async () => {
      try {
        const svgToSave = stringify(svgJoined);
        const base64Data = btoa(svgToSave);
        const dataUrl = `data:image/svg+xml;base64,${base64Data}`;

        const { res, url } = await uploadSVGToStorage(dataUrl, SVG_FOLDER);
        const filePath = res.ref.fullPath;
        await writeSVGToDatabase(filePath, "", { title: "", description: "" }, false, "uploaded", currentFile.workflow || "vector");

        const user = auth.currentUser;
        const uploadedFile = {
          id: res.ref.name,
          name: currentFile.name || res.ref.name,
          uri: res.ref,
          url,
          type: "uploaded",
          workflow: currentFile.workflow || "vector",
          ownerUid: user?.uid,
          isPublic: false,
          filePath,
          updatedAt: new Date().toISOString(),
        };

        setSvgStore((prev) => ({ ...prev, firebase: res.ref }));
        dispatch(addNewlyUploadedFile(uploadedFile));
        dispatch(
          setShowEditor({
            showEditor: true,
            currentFile: { ...uploadedFile, svgData: currentFile.svgData },
          }),
        );

        console.log("[useSaveSVG] Local SVG import uploaded to Firebase:", filePath);
      } catch (err) {
        console.error("[useSaveSVG] Background upload of local SVG failed:", err);
        autoUploadFileId = null;
      }
    })();
  }, [svgJoined, currentFile, svgStore]);

  const saveSVG = async () => {
    setLoading(true);
    setError(null);
    try {
      const svgToSave = stringify(svgJoined);
      const base64Data = btoa(unescape(encodeURIComponent(svgToSave)));
      const dataUrl = `data:image/svg+xml;base64,${base64Data}`;

      if (svgStore?.firebase) {
        const storageRef = svgStore.firebase;
        const response = await updateSVGInStorage(storageRef, dataUrl);
        dispatch(
          updateUploadedFileInRedux({ ...response, id: currentFile.id })
        );
      } else {
        const { res, url } = await uploadSVGToStorage(dataUrl, SVG_FOLDER);
        const filePath = res.ref.fullPath;
        const fileWorkflow = currentFile?.workflow || "vector";
        await writeSVGToDatabase(filePath, "", { title: "", description: "" }, false, "uploaded", fileWorkflow);

        const user = auth.currentUser;
        const uploadedFile = {
          id: res.ref.name,
          name: currentFile?.name || res.ref.name,
          uri: res.ref,
          url,
          type: "uploaded",
          workflow: fileWorkflow,
          ownerUid: user?.uid,
          isPublic: false,
          filePath,
          updatedAt: new Date().toISOString(),
        };

        setSvgStore((prev) => ({ ...prev, firebase: res.ref }));
        dispatch(addNewlyUploadedFile(uploadedFile));
        dispatch(
          setShowEditor({
            showEditor: true,
            currentFile: { ...uploadedFile, svgData: currentFile?.svgData },
          }),
        );

        console.log("[useSaveSVG] First save — uploaded to Firebase:", filePath);
      }
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return { saveSVG, loading, error, setLoading, setError };
};

export default useSaveSVG;
